import { createLowlight } from 'lowlight';
import json from 'highlight.js/lib/languages/json';
import yaml from 'highlight.js/lib/languages/yaml';
import bash from 'highlight.js/lib/languages/bash';
import javascript from 'highlight.js/lib/languages/javascript';
import typescript from 'highlight.js/lib/languages/typescript';
import sql from 'highlight.js/lib/languages/sql';
import dockerfile from 'highlight.js/lib/languages/dockerfile';
import python from 'highlight.js/lib/languages/python';
import go from 'highlight.js/lib/languages/go';
import ini from 'highlight.js/lib/languages/ini';
import xml from 'highlight.js/lib/languages/xml';
import diff from 'highlight.js/lib/languages/diff';

/**
 * Curated highlight.js grammar set, shared by BOTH highlighting surfaces so they
 * render identically: the TipTap code block (via `lowlight`, this instance) and
 * the read-only timeline/description (`<Markdown>` via `rehype-highlight`, which
 * takes `LANGUAGES`/`ALIASES` below). Chosen for infra/devops notes — extend
 * here to add a language everywhere at once. (highlight.js ships no HCL/Terraform
 * grammar, so those are intentionally absent.)
 */
export const LANGUAGES = {
  json,
  yaml,
  bash,
  javascript,
  typescript,
  sql,
  dockerfile,
  python,
  go,
  ini,
  xml,
  diff,
};

/** Short fence aliases → canonical grammar, so ```yml / ```sh / ```js / ```py all highlight. */
export const ALIASES: Record<string, string[]> = {
  yaml: ['yml'],
  bash: ['sh', 'shell', 'zsh'],
  javascript: ['js'],
  typescript: ['ts'],
  python: ['py'],
  xml: ['html'],
  dockerfile: ['docker'],
  ini: ['toml'],
};

export const lowlight = createLowlight(LANGUAGES);

/**
 * The dropdown value for a fence tag: `ts` → `typescript`, `yml` → `yaml`, case-folded.
 * Unknown tags come back unchanged (the dropdown then shows them as-is). The node keeps
 * its original tag until the user picks another language, so ```ts saves as ```ts.
 */
export function canonicalLanguage(tag: string): string {
  const t = tag.trim().toLowerCase();
  if (t in LANGUAGES) return t;
  return Object.keys(ALIASES).find((k) => ALIASES[k].includes(t)) ?? tag;
}
lowlight.registerAlias(ALIASES);

/** Options for the per-block language dropdown (label shown → fence value stored). `''` = no highlighting. */
export const CODE_LANGUAGES: { value: string; label: string }[] = [
  { value: '', label: 'Plain text' },
  { value: 'bash', label: 'Bash / Shell' },
  { value: 'json', label: 'JSON' },
  { value: 'yaml', label: 'YAML' },
  { value: 'javascript', label: 'JavaScript' },
  { value: 'typescript', label: 'TypeScript' },
  { value: 'python', label: 'Python' },
  { value: 'go', label: 'Go' },
  { value: 'sql', label: 'SQL' },
  { value: 'dockerfile', label: 'Dockerfile' },
  { value: 'ini', label: 'INI / TOML' },
  { value: 'xml', label: 'XML / HTML' },
  { value: 'diff', label: 'Diff' },
];
